import type { V1DraftResponse } from '@aira/shared';
import type { ProgressGroup, ProgressStep } from '../api/ragBackend';
import { isWorkflowStorageExpired } from './workflowStorage';

const DIALOG_SESSION_PREFIX = 'aira-outlook-dialog:';

export interface DialogSession {
  subject: string;
  body: string;
  from: string;
  name: string;
  instructions: string;
  message_id: string | null;
  received_at: string | null;
  run_id: string | null;
  operation_id?: string | null;
  refine_error?: string | null;
  insert_requested?: boolean;
  response: V1DraftResponse | null;
  progress: ProgressStep[];
  grouped_progress: ProgressGroup[];
  regenerate_count: number;
  closed: boolean;
  updated_at: number;
}

type DialogSessionUpdate = Partial<Omit<DialogSession, 'updated_at'>>;

export function dialogSessionKey(seedId: string): string {
  return `${DIALOG_SESSION_PREFIX}${seedId}`;
}

function isDialogSession(value: unknown): value is DialogSession {
  if (typeof value !== 'object' || value === null) return false;
  const session = value as Partial<DialogSession>;
  const validResponse =
    session.response === null ||
    (typeof session.response === 'object' &&
      typeof session.response.trace_id === 'string' &&
      typeof session.response.draft === 'string');
  return (
    typeof session.subject === 'string' &&
    typeof session.body === 'string' &&
    typeof session.from === 'string' &&
    typeof session.name === 'string' &&
    typeof session.instructions === 'string' &&
    (session.message_id === null || typeof session.message_id === 'string') &&
    (session.received_at === null || typeof session.received_at === 'string') &&
    (session.run_id === null || typeof session.run_id === 'string') &&
    (session.operation_id === undefined ||
      session.operation_id === null ||
      typeof session.operation_id === 'string') &&
    (session.refine_error === undefined ||
      session.refine_error === null ||
      typeof session.refine_error === 'string') &&
    (session.insert_requested === undefined || typeof session.insert_requested === 'boolean') &&
    validResponse &&
    Array.isArray(session.progress) &&
    Array.isArray(session.grouped_progress) &&
    typeof session.regenerate_count === 'number' &&
    typeof session.closed === 'boolean' &&
    typeof session.updated_at === 'number'
  );
}

export function readDialogSession(seedId: string): DialogSession | null {
  const key = dialogSessionKey(seedId);
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!isDialogSession(parsed) || isWorkflowStorageExpired(parsed.updated_at)) {
      window.localStorage.removeItem(key);
      return null;
    }
    return parsed;
  } catch {
    // A malformed session cannot be recovered safely and should not retain
    // email or draft content indefinitely.
    try {
      window.localStorage.removeItem(key);
    } catch {
      // Ignore restricted-storage hosts.
    }
    return null;
  }
}

export function createDialogSession(
  seedId: string,
  session: Omit<DialogSession, 'updated_at'>,
): void {
  try {
    window.localStorage.setItem(
      dialogSessionKey(seedId),
      JSON.stringify({ ...session, updated_at: Date.now() }),
    );
  } catch {
    // The Office messaging channel remains available when storage is restricted.
  }
}

export function updateDialogSession(seedId: string | null, update: DialogSessionUpdate): void {
  if (!seedId) return;
  try {
    const existing = readDialogSession(seedId);
    if (!existing) return;
    window.localStorage.setItem(
      dialogSessionKey(seedId),
      JSON.stringify({ ...existing, ...update, updated_at: Date.now() }),
    );
  } catch {
    // The Office messaging channel remains available when storage is restricted.
  }
}

/**
 * The Office Dialog window is a separate popup — the user can keep clicking
 * through emails in the main Outlook window while it drafts. A session with
 * no recorded `message_id` (legacy/unknown) is treated as matching, since
 * there is nothing to verify against; that preserves the pre-existing
 * behavior for those cases instead of blocking them.
 */
export function dialogSessionMatchesMessageId(
  session: Pick<DialogSession, 'message_id'> | null,
  currentMessageId: string | null | undefined,
): boolean {
  const originMessageId = session?.message_id ?? null;
  return !originMessageId || originMessageId === (currentMessageId ?? null);
}

export function removeDialogSession(seedId: string | null): void {
  if (!seedId) return;
  try {
    window.localStorage.removeItem(dialogSessionKey(seedId));
  } catch {
    // Ignore restricted-storage hosts.
  }
}
